import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';
import { NavLink } from 'react-router-dom';
import { usePWAStore } from '../../store/pwaStore';
import { useTranslation } from 'react-i18next';
import {
  HomeIcon,
  BanknotesIcon,
  ClipboardDocumentCheckIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  FolderIcon,
  DocumentChartBarIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'nav.dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'nav.timeEntry', href: '/time-entry', icon: ClockIcon },
  { name: 'nav.tasks', href: '/tasks', icon: ClipboardDocumentCheckIcon },
  { name: 'nav.expenses', href: '/expenses', icon: BanknotesIcon },
  { name: 'nav.clients', href: '/clients', icon: UserGroupIcon },
  { name: 'nav.projects', href: '/projects', icon: FolderIcon },
  { name: 'nav.search', href: '/search', icon: MagnifyingGlassIcon },
  { name: 'nav.ask', href: '/ask', icon: SparklesIcon },
  { name: 'nav.reports', href: '/reports', icon: DocumentChartBarIcon },
];

export const MobileSidebar: React.FC = () => {
  const { t } = useTranslation();
  const { isMobileSidebarOpen, setMobileSidebarOpen } = usePWAStore();

  const handleNavigate = () => {
    setMobileSidebarOpen(false);
  };

  return (
    <Transition.Root show={isMobileSidebarOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50 lg:hidden" onClose={setMobileSidebarOpen}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/80" />
        </Transition.Child>

        <div className="fixed inset-0 flex">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                  <button
                    type="button"
                    className="-m-2.5 p-2.5"
                    onClick={() => setMobileSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </Transition.Child>

              <div className="flex grow flex-col gap-y-2 overflow-y-auto bg-white dark:bg-gray-800 px-0 pb-4">
                {/* Logo */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <NavLink
                    to="/dashboard"
                    onClick={handleNavigate}
                    className="flex items-center"
                  >
                    <span className="text-xl font-bold text-gray-900 dark:text-white">Auxilio</span>
                    <img src="/favicon.svg" alt="" className="h-6 w-6 mx-1" />
                    <span className="text-xl font-bold text-gray-900 dark:text-white">Time</span>
                  </NavLink>
                </div>

                {/* Navigation */}
                <nav className="flex-1 px-4">
                  <ul className="space-y-1">
                    {navigation.map((item) => (
                      <li key={item.href}>
                        <NavLink
                          to={item.href}
                          onClick={handleNavigate}
                          className={({ isActive }) =>
                            `flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                              isActive
                                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200'
                                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                            }`
                          }
                        >
                          <item.icon className="h-5 w-5 mr-3" />
                          {t(item.name)}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
